/**
 * Service Coverage Utility
 * سیستم قابل توسعه برای بررسی پوشش سرویس‌دهی
 */

/**
 * Ray-casting algorithm for point-in-polygon test
 * الگوریتم ray-casting برای تشخیص نقطه درون چندضلعی
 */
function isPointInPolygon(point, polygon) {
  if (!Array.isArray(point) || !Array.isArray(polygon)) {
    return false;
  }

  const x = point[0];
  const y = point[1];

  if (typeof x !== 'number' || typeof y !== 'number') {
    return false;
  }

  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];

    if (
      typeof xi !== 'number' ||
      typeof yi !== 'number' ||
      typeof xj !== 'number' ||
      typeof yj !== 'number'
    ) {
      continue;
    }

    const intersect =
      (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Check if a point is inside any polygon from GeoJSON data
 * بررسی قرار گرفتن نقطه در هر یک از چندضلعی‌های GeoJSON
 *
 * Supports: Polygon, MultiPolygon, and closed LineString
 */
function isPointInGeoJSON(point, geojsonData) {
  if (!geojsonData || !geojsonData.features) {
    return false;
  }

  for (const feature of geojsonData.features) {
    if (!feature.geometry) continue;

    const { type, coordinates } = feature.geometry;

    // Handle Polygon
    if (type === 'Polygon') {
      const exteriorRing = coordinates[0];
      if (isPointInPolygon(point, exteriorRing)) {
        return true;
      }
    }

    // Handle MultiPolygon
    else if (type === 'MultiPolygon') {
      for (const polygon of coordinates) {
        const exteriorRing = polygon[0];
        if (isPointInPolygon(point, exteriorRing)) {
          return true;
        }
      }
    }

    // Handle closed LineString (first and last points are the same)
    else if (type === 'LineString') {
      // Check if LineString is closed (forms a polygon)
      const firstPoint = coordinates[0];
      const lastPoint = coordinates[coordinates.length - 1];

      if (
        firstPoint &&
        lastPoint &&
        firstPoint[0] === lastPoint[0] &&
        firstPoint[1] === lastPoint[1]
      ) {
        // It's a closed LineString, treat it as a polygon
        if (isPointInPolygon(point, coordinates)) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Service Provider Definition
 * تعریف سرویس‌دهنده
 *
 * @typedef {Object} ServiceProvider
 * @property {string} id - شناسه یکتا
 * @property {string} name - نام سرویس‌دهنده
 * @property {string} successMessage - پیغام موفقیت
 * @property {string} failureMessage - پیغام عدم پوشش
 * @property {Function} checkCoverage - تابع بررسی پوشش
 */

/**
 * Default service providers
 * سرویس‌دهندگان پیش‌فرض
 */
const serviceProviders = [
  {
    id: 'mobinnet',
    name: 'مبین‌نت',
    successMessage: 'تحت پوشش توسط سرویس دهنده مبین‌نت',
    failureMessage: 'متاسفانه در حال حاضر هیچ سرویس دهنده‌ای در این منطقه سرویس ارائه نمی‌دهد.',

    /**
     * Check if point is covered by Mobinnet
     * @param {Array<number>} point - [lng, lat]
     * @param {Object} polygonData - GeoJSON polygon data
     * @returns {boolean}
     */
    checkCoverage: (point, polygonData) => {
      return isPointInGeoJSON(point, polygonData);
    }
  }
  // در آینده می‌توانید سرویس‌دهندگان دیگر را اضافه کنید:
  // {
  //   id: 'other-provider',
  //   name: 'سرویس دهنده دیگر',
  //   successMessage: 'پیغام موفقیت',
  //   failureMessage: 'پیغام عدم پوشش',
  //   checkCoverage: (point, data) => {
  //     // منطق دلخواه شما
  //     return true/false;
  //   }
  // }
];

/**
 * Add a new service provider
 * افزودن سرویس‌دهنده جدید
 */
export function addServiceProvider(provider) {
  if (!provider.id || !provider.name || !provider.checkCoverage) {
    throw new Error('Service provider must have id, name, and checkCoverage function');
  }
  serviceProviders.push(provider);
}

/**
 * Check service coverage for a point
 * بررسی پوشش سرویس برای یک نقطه
 *
 * @param {number} lat - عرض جغرافیایی
 * @param {number} lng - طول جغرافیایی
 * @param {Object} coverageData - داده‌های پوشش (GeoJSON polygons)
 * @param {string} providerId - شناسه سرویس‌دهنده (اختیاری، پیش‌فرض: اولین provider)
 * @returns {Object} - {covered: boolean, message: string, provider: string}
 */
export function checkServiceCoverage(lat, lng, coverageData, providerId = null) {
  const point = [lng, lat];

  // Find the provider
  let provider = serviceProviders[0]; // default to first provider
  if (providerId) {
    const found = serviceProviders.find(p => p.id === providerId);
    if (found) provider = found;
  }

  try {
    const isCovered = provider.checkCoverage(point, coverageData);

    return {
      covered: isCovered,
      message: isCovered ? provider.successMessage : provider.failureMessage,
      provider: provider.name
    };
  } catch (error) {
    console.error('Error checking service coverage:', error);
    return {
      covered: false,
      message: 'خطا در بررسی پوشش سرویس',
      provider: provider.name
    };
  }
}

/**
 * Get all available service providers
 * دریافت تمام سرویس‌دهندگان موجود
 */
export function getServiceProviders() {
  return serviceProviders;
}

const serviceCoverageUtils = {
  checkServiceCoverage,
  addServiceProvider,
  getServiceProviders,
  isPointInPolygon,
  isPointInGeoJSON
};

export default serviceCoverageUtils;
